import {
  type FieldMetadata,
  getCollectionProps as conformGetCollectionProps,
  getInputProps as conformGetInputProps,
  getSelectProps as conformGetSelectProps,
  useInputControl,
} from '@conform-to/react';
import { useControl } from '@conform-to/react/future';
import { useId } from 'react';

import { type IconProps } from '@veraclins-dev/ui/icon';
import { type IconName } from '@veraclins-dev/ui/icons';
import { type LabelProps } from '@veraclins-dev/ui/label';
import { type MaybeString } from '@veraclins-dev/ui/types';
import { slugify } from '@veraclins-dev/utils';

// `name`/`form` are optional (unlike conform's return type): the unbound
// branch has no form id and possibly no name, and must omit them rather than
// emit `name=""` / the invalid-IDREF `form=""` on the DOM element.
type InputFieldProps = Omit<
  ReturnType<typeof conformGetInputProps>,
  'name' | 'form'
> & {
  name?: string;
  form?: string;
};

type Params = Parameters<typeof conformGetInputProps>;

type BaseInputProps<S = MaybeString> = {
  leftIcon?: IconProps | IconName;
  rightIcon?: IconProps | IconName;
  rightAddon?: React.ReactNode;
  name?: string;
  label?: string | React.ReactNode;
  topText?: string | React.ReactNode;
  field?: FieldMetadata<S>;
  // Full DS Label props (not just LabelHTMLAttributes) so field-integrated
  // labels can pass `variant` (e.g. `variant="caption"`) and other Label
  // props, which InputWrapper forwards to the DS <Label>.
  labelProps?: LabelProps;
  inputClass?: string;
  wrapperClassName?: string;
};

type GetPropsOptions<S = MaybeString> = {
  field?: FieldMetadata<S>;
  name?: InputFieldProps['name'];
  id?: InputFieldProps['id'];
} & Partial<Omit<Params[1], 'ariaAttributes'>>;

const getInputProps = <S = MaybeString>({
  type = 'text',
  field,
  name,
  id,
}: GetPropsOptions<S>): InputFieldProps & {
  'aria-label'?: string;
} => {
  if (!field) {
    // No `name`/`form` fallbacks: React drops undefined-valued attributes, so
    // the rendered input carries neither when they are not supplied.
    return {
      type,
      key: undefined,
      defaultValue: undefined,
      name,
      'aria-label': name,
      id: id ?? '',
    };
  }

  const props = conformGetInputProps(field, { ariaAttributes: true, type });
  if (!props.name) {
    props.name = field.name ?? name;
  }
  if (props.maxLength) {
    delete props.maxLength;
  }
  if (!props.id) {
    props.id = field.id ?? id;
  }
  return props;
};

function useFieldProperties<S = string>(field?: BaseInputProps<S>['field']) {
  const fallbackId = useId();
  const id = field?.id ?? field?.name ?? fallbackId;
  const labelId = `${id}-label`;
  const hasErrors = !!field?.errors?.length;
  return {
    errorId: hasErrors ? `${id}-error` : undefined,
    errors: field?.errors,
    id,
    labelId,
  };
}

// Same widening as {@link InputFieldProps}: the standalone branch must be able
// to omit `name`/`form` instead of emitting empty-string attributes.
type CheckboxCollectionItemProps = Omit<
  ReturnType<typeof conformGetCollectionProps>[number],
  'name' | 'form'
> & {
  name?: string;
  form?: string;
};

/**
 * Per-item props for a standard multi-checkbox group. Each returned entry is a
 * native `<input type="checkbox">` sharing the same `name` with its own `value`,
 * so the browser submits repeated entries that conform coerces to `string[]`.
 * Falls back to manual props (mirroring {@link getInputProps}) when no `field`
 * is bound, so the group also works standalone.
 */
const getCheckboxCollectionProps = (
  values: string[],
  field?: FieldMetadata<string[]>,
  options?: { name?: string; id?: string; defaultValue?: string[] },
): CheckboxCollectionItemProps[] => {
  if (field) {
    return conformGetCollectionProps(field, {
      type: 'checkbox',
      options: values,
    });
  }

  const { name, id, defaultValue } = options ?? {};
  return values.map((value) => ({
    type: 'checkbox' as const,
    key: value,
    id: id ? `${id}-${slugify(value)}` : slugify(value),
    name,
    value,
    defaultChecked: defaultValue?.includes(value) || undefined,
  }));
};

type SelectParams = Parameters<typeof conformGetSelectProps>;

const getSelectProps = (field: SelectParams[0]) => {
  const props = conformGetSelectProps(field);
  if (!props.name) {
    props.name = field.name;
  }

  return props;
};

type ControlParams = Parameters<typeof useInputControl>;

const useSelectControlProps = (field?: ControlParams[0], name?: string) => {
  const control = useInputControl(field ?? { formId: '', name: name ?? '' });
  if (!field) return { value: undefined };

  return {
    onValueChange: (value: string) => {
      control.change(value);
    },
    onOpenChange: (open: boolean) => {
      if (!open) {
        control.blur();
      }
    },
    value: control.value as string | undefined,
  };
};

type Value = ReturnType<typeof useInputControl>['value'];

const useInputControlProps = <S extends Value = string>(
  field?: ControlParams[0],
  name?: string,
) => {
  const control = useInputControl(field ?? { formId: '', name: name ?? '' });
  if (!field) {
    return {
      value: undefined,
      onChange: () => {
        // empty function
      },
      onBlur: () => {
        // empty function
      },
    };
  }

  return {
    onChange: (value?: string) => {
      control.change(value);
    },
    onBlur: () => {
      control.blur();
    },
    control,
    value: control.value as S,
  };
};

export const useControlProps = useControl;

export {
  type BaseInputProps,
  type CheckboxCollectionItemProps,
  getCheckboxCollectionProps,
  getInputProps,
  getSelectProps,
  type InputFieldProps,
  useFieldProperties,
  useInputControlProps,
  useSelectControlProps,
};
